import type { UploadFile } from "antd/es/upload/interface";
import { mediaConstants } from "../actions/constants/mediaConstants";

interface UploadedMediaItem {
  name: string;
  size: number;
  url?: string;
  thumbUrl?: string;
}

interface MediaUploadState {
  fileList: UploadFile[];
  uploadedFiles: UploadedMediaItem[];
  loading: boolean;
  /** Tracks how many upload batches are currently in-flight. */
  pendingUploads: number;
  error: any;
  success: boolean;
  uploadProgress: Record<string, number>;
  mediaStatusLoading: boolean;
  mediaStatusSuccess: boolean;
  mediaStatusError: any;
  mediaStatusData: any;
}

const initialState: MediaUploadState = {
  fileList: [],
  uploadedFiles: [],
  loading: false,
  pendingUploads: 0,
  error: null,
  success: false,
  uploadProgress: {},
  mediaStatusLoading: false,
  mediaStatusSuccess: false,
  mediaStatusError: null,
  mediaStatusData: null,
};

export const mediaUploadReducer = (state = initialState, action: any): MediaUploadState => {
  switch (action.type) {
    case mediaConstants.addFilesToList:
      return {
        ...state,
        fileList: [...state.fileList, ...action.payload],
      };

    case mediaConstants.removeFileFromList:
      return {
        ...state,
        fileList: state.fileList.filter((file) => file.uid !== action.payload),
      };

    case mediaConstants.uploadMediaRequest:
      return {
        ...state,
        pendingUploads: state.pendingUploads + 1,
        loading: true,
        error: null,
        success: false,
        // Only wipe progress when starting a brand-new upload session
        uploadProgress: state.pendingUploads === 0 ? {} : state.uploadProgress,
      };

    case mediaConstants.uploadMediaProgress:
      return {
        ...state,
        uploadProgress: {
          ...state.uploadProgress,
          [action.payload.fileName]: action.payload.percentage,
        },
      };

    case mediaConstants.uploadMediaSuccess: {
      const pendingAfterSuccess = Math.max(0, state.pendingUploads - 1);
      return {
        ...state,
        pendingUploads: pendingAfterSuccess,
        loading: pendingAfterSuccess > 0,
        success: pendingAfterSuccess === 0,
        uploadedFiles: [...state.uploadedFiles, ...action.payload.success],
        // fileList is cleaned up by individual removeFileFromList dispatches
        error: null,
      };
    }

    case mediaConstants.uploadMediaFailure: {
      const pendingAfterFailure = Math.max(0, state.pendingUploads - 1);
      return {
        ...state,
        pendingUploads: pendingAfterFailure,
        loading: pendingAfterFailure > 0,
        error: action.payload,
        success: false,
      };
    }

    case mediaConstants.clearUploadMediaState:
      return initialState;

    case mediaConstants.updateMediaStatusRequest:
      return {
        ...state,
        mediaStatusLoading: true,
        mediaStatusSuccess: false,
        mediaStatusError: null,
      };

    case mediaConstants.updateMediaStatusSuccess:
      return {
        ...state,
        mediaStatusLoading: false,
        mediaStatusSuccess: true,
        mediaStatusError: null,
        mediaStatusData: action.payload,
      };

    case mediaConstants.updateMediaStatusFailure:
      return {
        ...state,
        mediaStatusLoading: false,
        mediaStatusSuccess: false,
        mediaStatusError: action.payload,
      };

    default:
      return state;
  }
};

export const mediaStatusReducer = (state = initialState, action: any): MediaUploadState => {
  switch (action.type) {
    case mediaConstants.updateMediaStatusRequest:
      return {
        ...state,
        mediaStatusLoading: true,
        mediaStatusSuccess: false,
        mediaStatusError: null,
      };

    case mediaConstants.updateMediaStatusSuccess:
      return {
        ...state,
        mediaStatusLoading: false,
        mediaStatusSuccess: true,
        mediaStatusError: null,
        mediaStatusData: action.payload,
      };

    case mediaConstants.updateMediaStatusFailure:
      return {
        ...state,
        mediaStatusLoading: false,
        mediaStatusSuccess: false,
        mediaStatusError: action.payload,
      };

    default:
      return state;
  }
}


interface ListMediaState {
  mediaList: any[];
  nextContinuationTokenImages: string | null;
  nextContinuationTokenVideos: string | null;
  hasMoreImages: boolean;
  hasMoreVideos: boolean;
  loading: boolean;
  loadingMore: boolean;
  error: any;
  success: boolean;
}

const listMediaInitialState: ListMediaState = {
  mediaList: [],
  nextContinuationTokenImages: null,
  nextContinuationTokenVideos: null,
  hasMoreImages: false,
  hasMoreVideos: false,
  loading: false,
  loadingMore: false,
  error: null,
  success: false,
};

export const listMediaReducer = (state = listMediaInitialState, action: any): ListMediaState => {
  switch (action.type) {
    case mediaConstants.listMediaRequest:
      if (action.payload?.append) {
        return { ...state, loadingMore: true, error: null };
      }
      // Fresh load — reset list and all pagination cursors
      return {
        ...state,
        loading: true,
        mediaList: [],
        nextContinuationTokenImages: null,
        nextContinuationTokenVideos: null,
        hasMoreImages: false,
        hasMoreVideos: false,
        error: null,
      };

    case mediaConstants.listMediaSuccess: {
      const {
        items,
        append,
        nextContinuationTokenImages,
        nextContinuationTokenVideos,
        hasMoreImages,
        hasMoreVideos,
      } = action.payload;
      return {
        ...state,
        loading: false,
        loadingMore: false,
        success: true,
        mediaList: append ? [...state.mediaList, ...items] : items,
        // Only overwrite tokens returned by this particular request type
        nextContinuationTokenImages:
          nextContinuationTokenImages !== undefined
            ? nextContinuationTokenImages
            : state.nextContinuationTokenImages,
        nextContinuationTokenVideos:
          nextContinuationTokenVideos !== undefined
            ? nextContinuationTokenVideos
            : state.nextContinuationTokenVideos,
        hasMoreImages: hasMoreImages ?? false,
        hasMoreVideos: hasMoreVideos ?? false,
        error: null,
      };
    }

    case mediaConstants.listMediaFailure:
      return {
        ...state,
        loading: false,
        loadingMore: false,
        error: action.payload,
        success: false,
      };

    default:
      return state;
  }
};